<template>
  <div class="maintenance-view">
    <MaintenanceTopbar
      :park="park"
      :loading="loading"
      :alerts="alerts"
      :technicians="technicians"
      :counts="counts"
      v-model:active-tab="activeTab"
      v-model:type-filters="typeFilters"
      v-model:technician-filters="technicianFilters"
      v-model:search="searchText"
      :search-results="searchResults"
      :search-loading="searchLoading"
      @open-technician-manager="onOpenTechnicianManager"
      @new-request="onNewRequest"
      @search-select="onSearchSelect"
      @open-notifications="onOpenNotifications"
      :technician-manager-open="showTechnicianManager"
    />

    <div v-if="loading" class="maintenance-loading">
      <span class="mdi mdi-loading maintenance-loading__spinner"></span>
      <span class="maintenance-loading__text">Cargando mantenimiento...</span>
    </div>

    <div v-else class="maintenance-view__body">
      <div class="maintenance-view__main">
        <MaintenanceMetricsRow
          v-if="metrics && activeTab !== 'preventivo'"
          :metrics="metrics"
          @filter-open="activeTab = 'abiertos'"
          @filter-sla-risk="onFilterSlaRisk"
          @open-resolution-analysis="onOpenResolutionAnalysis"
          @filter-cost="onFilterCost"
        />

        <PreventiveMaintenanceCalendar
          v-if="activeTab === 'preventivo' && park"
          :park-id="park.id"
          :technicians="technicians"
          :plots="park.plots || []"
          @open-ticket="onOpenTicket"
        />

        <MaintenanceListView
          v-else-if="park"
          :park-id="park.id"
          :search="searchText"
          :type-filters="typeFilters"
          :technician-filters="technicianFilters"
          :technicians="technicians"
          :status-filter="activeTab"
          @open-ticket="onOpenTicket"
          @assign-technician="onAssignTechnicianClick"
          @change-priority="onChangePriority"
          @register-cost="onRegisterCost"
          @cancel-ticket="onCancelTicket"
          @batch-action="onBatchAction"
          @tickets-updated="onTicketsUpdated"
        />
      </div>

      <Transition name="panel-slide">
          <MaintenanceTicketPanel
            v-if="selectedTicketId"
            :ticket-id="selectedTicketId"
            :ticket-list="allTicketsForNav"
            @close="selectedTicketId = null"
            @navigate="selectedTicketId = $event"
            @refresh="onRefreshBoard"
          />
          <NewTicketPanel
            v-else-if="showNewTicketPanel && park"
            :park-id="park.id"
            :initial-status="newTicketInitialStatus"
            :initial-plot="newTicketInitialPlot"
            @close="showNewTicketPanel = false"
            @created="onTicketCreated"
          />
          <TechnicianManagerPanel
              v-else-if="showTechnicianManager && park"
              :park-id="park.id"
              @close="showTechnicianManager = false"
              @updated="onTechniciansUpdated"
            />
        </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import MaintenanceTopbar from '@/components/Maintenance/MaintenanceTopBar.vue'
import { getParks, getParkById } from '@/services/parks'
import { getParkAlerts } from '@/services/alerts'
import { getTechnicians, getTicketCounts, searchTickets } from '@/services/maintenance'
import MaintenanceMetricsRow from '@/components/Maintenance/MaintenanceMetricsRow.vue'
import { getMaintenanceMetrics } from '@/services/maintenance'
import MaintenanceListView from '@/components/Maintenance/MaintenanceListView.vue'
import MaintenanceTicketPanel from '@/components/Maintenance/MaintenanceTicketPanel.vue'
import NewTicketPanel from '@/components/Maintenance/NewTicketPanel.vue'
import TechnicianManagerPanel from '@/components/Maintenance/TechnicianManagerPanel.vue'
import PreventiveMaintenanceCalendar from '@/components/Maintenance/PreventiveMaintenanceCalendar.vue'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()
const newTicketInitialPlot = ref(null)

const showTechnicianManager = ref(false)

function onOpenTechnicianManager() {
  selectedTicketId.value = null
  showNewTicketPanel.value = false
  showTechnicianManager.value = !showTechnicianManager.value
}

async function onTechniciansUpdated() {
  technicians.value = await getTechnicians(park.value.id)
}

const newTicketInitialStatus = ref(null)

function onNewRequest() {
  selectedTicketId.value = null
  newTicketInitialStatus.value = null
  showNewTicketPanel.value = true
}

const showNewTicketPanel = ref(false)

async function onTicketCreated(ticket) {
  showNewTicketPanel.value = false
  selectedTicketId.value = ticket.id
  await onRefreshBoard()
}

const selectedTicketId = ref(null)
const allTicketsForNav = ref([])

function onOpenTicket(ticketId) {
  selectedTicketId.value = ticketId
}

async function onRefreshBoard() {
  if (!park.value) return
  const [countsData, metricsData] = await Promise.all([
    getTicketCounts(park.value.id),
    getMaintenanceMetrics(park.value.id),
  ])
  counts.value = countsData
  metrics.value = metricsData
}

function onTicketsUpdated(tickets) {
  allTicketsForNav.value = tickets
  onRefreshBoard()
}

function onChangePriority(ticketId) {
  selectedTicketId.value = ticketId
}

function onRegisterCost(ticketId) {
  selectedTicketId.value = ticketId
}

function onCancelTicket(ticketId) {
  selectedTicketId.value = ticketId
}

function onBatchAction({ action }) {
  console.warn(`Acción por lote "${action}" todavía no está implementada`)
}

const metrics = ref(null)

const park = ref(null)
const loading = ref(true)
const alerts = ref([])
const technicians = ref([])

const activeTab = ref('abiertos')
const typeFilters = ref([])
const technicianFilters = ref([])

const counts = ref({ abiertos: 0, enProceso: 0, enEspera: 0, resueltos: 0, todos: 0, hasUrgentUnassigned: false })

const searchText = ref('')
const searchResults = ref({ activos: [], resueltos: [], porNave: [], porTecnico: [] })
const searchLoading = ref(false)

onMounted(async () => {
  try {
    const parks = await getParks()
    if (parks[0]) {
      const [fullPark, alertsData, countsData, techniciansData, metricsData] = await Promise.all([
        getParkById(parks[0].id),
        getParkAlerts(parks[0].id),
        getTicketCounts(parks[0].id),
        getTechnicians(parks[0].id),
        getMaintenanceMetrics(parks[0].id),
      ])
      park.value = fullPark
      alerts.value = alertsData
      counts.value = countsData
      technicians.value = techniciansData
      metrics.value = metricsData
    }
  } finally {
    loading.value = false
  }

  // Llegada desde un contrato ("Registrar incidencia"): se abre el formulario con la nave ya elegida.
  if (route.query.nueva === '1' && park.value) {
    newTicketInitialPlot.value = route.query.plotId ? { id: String(route.query.plotId), name: String(route.query.nave || '') } : null
    onNewRequest()
    router.replace({ path: route.path })
  }
})

let debounceHandle = null
watch(searchText, (query) => {
  if (debounceHandle) clearTimeout(debounceHandle)

  if (!query.trim()) {
    searchResults.value = { activos: [], resueltos: [], porNave: [], porTecnico: [] }
    searchLoading.value = false
    return
  }

  searchLoading.value = true
  debounceHandle = setTimeout(async () => {
    if (!park.value) return
    try {
      searchResults.value = await searchTickets(park.value.id, query)
    } finally {
      searchLoading.value = false
    }
  }, 250)
})


// TODO: conectar cuando exista el tablero (filtrar por el resultado seleccionado)
function onSearchSelect({ type, item }) {
  console.log('Resultado seleccionado:', type, item)
}

// TODO: conectar cuando exista el panel de notificaciones
function onOpenNotifications() {
  console.log('Abrir notificaciones')
}

function onFilterSlaRisk() {
  // TODO: el tablero necesita un modo de orden "por urgencia de SLA" que no existe todavía
  console.log('Filtrar por SLA en riesgo')
}

function onOpenResolutionAnalysis() {
  // TODO: mini panel de distribución de tiempos por técnico, pendiente de construir
  console.log('Abrir análisis de tiempos de resolución')
}

function onFilterCost() {
  // TODO: filtro de tickets con costo + resumen por técnico en panel lateral, pendiente
  console.log('Filtrar por costo del mes')
}

function onAssignTechnicianClick(ticketId) {
  // TODO: selector rápido de técnico, pendiente
  console.log('Asignar técnico a:', ticketId)
}
</script>

<style scoped>
.maintenance-view__body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.maintenance-view__main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: transform 250ms ease-out, opacity 250ms ease-out;
}

.panel-slide-enter-from,
.panel-slide-leave-to {
  transform: translateX(24px);
  opacity: 0;
}
.maintenance-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.maintenance-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-secondary);
}

.maintenance-loading__spinner {
  font-size: 1.8rem;
  animation: maintenance-spin 0.8s linear infinite;
}

.maintenance-loading__text {
  font-size: 0.85rem;
  font-family: var(--font-body);
}

@keyframes maintenance-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>